import { useMemo, useRef, useState } from 'react';
import { importTimetableCsv, importTimetableJson, type TimetableAct } from '@lir/domain';

import { inspectTimetableImport, type ParsedImport } from '../lib/timetable-import';
import type {
  EditionDraftV1,
  RevisionSummary,
  ValidationIssue,
  ValidationResult,
} from '../types';

interface DraftEditorProps {
  draft: EditionDraftV1;
  validation: ValidationResult | undefined;
  revisions: RevisionSummary[];
  validating: boolean;
  publishing: boolean;
  importing: boolean;
  onChange: (draft: EditionDraftV1) => void;
  onValidate: () => void;
  onPublish: () => void;
  onRestore: (releaseId: string) => void;
  onImport: (source: string, format: 'csv' | 'json') => Promise<void>;
  onFocusIssue: (issue: ValidationIssue) => void;
}

function toLocalInput(iso: string): string {
  const date = new Date(iso);
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

function fromLocalInput(value: string): string {
  return new Date(value).toISOString();
}

function makeId(prefix: string, label: string): string {
  const slug = label
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
    .slice(0, 60);
  return `${prefix}-${slug || Date.now().toString(36)}`;
}

function localPreview(source: string, format: 'csv' | 'json') {
  return format === 'csv' ? importTimetableCsv(source) : importTimetableJson(source);
}

export function DraftEditor({
  draft,
  validation,
  revisions,
  validating,
  publishing,
  importing,
  onChange,
  onValidate,
  onPublish,
  onRestore,
  onImport,
  onFocusIssue,
}: DraftEditorProps) {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importSource, setImportSource] = useState('');
  const [importFilename, setImportFilename] = useState('timetable.csv');
  const [importInspection, setImportInspection] = useState<ParsedImport>();
  const [importError, setImportError] = useState<string>();
  const fileRef = useRef<HTMLInputElement>(null);

  const sortedActs = useMemo(
    () => [...draft.timetable].sort((left, right) => left.startsAt.localeCompare(right.startsAt)),
    [draft.timetable],
  );
  const issueCounts = useMemo(
    () => ({
      errors: validation?.issues.filter((issue) => issue.severity === 'error').length ?? 0,
      warnings: validation?.issues.filter((issue) => issue.severity === 'warning').length ?? 0,
    }),
    [validation],
  );

  const updateEdition = (patch: Partial<EditionDraftV1['edition']>) =>
    onChange({ ...draft, edition: { ...draft.edition, ...patch } });

  const updateStage = (stageId: string, patch: Partial<EditionDraftV1['stages'][number]>) =>
    onChange({
      ...draft,
      stages: draft.stages.map((stage) => (stage.id === stageId ? { ...stage, ...patch } : stage)),
    });

  const removeStage = (stageId: string) => {
    if (draft.timetable.some((act) => act.stageId === stageId)) return;
    onChange({ ...draft, stages: draft.stages.filter((stage) => stage.id !== stageId) });
  };

  const addStage = () => {
    const count = draft.stages.length + 1;
    const id = makeId('stage', `buehne-${count}`);
    onChange({
      ...draft,
      stages: [
        ...draft.stages,
        { id, name: { de: `Bühne ${count}` }, shortName: `B${count}`, color: '#eb4d8a' },
      ],
    });
  };

  const updateAct = (actId: string, patch: Partial<TimetableAct>) =>
    onChange({
      ...draft,
      timetable: draft.timetable.map((act) => (act.id === actId ? { ...act, ...patch } : act)),
    });

  const addAct = () => {
    const startsAt = new Date(draft.edition.startsAt);
    const endsAt = new Date(startsAt.getTime() + 60 * 60_000);
    const id = `act-${Date.now().toString(36)}`;
    onChange({
      ...draft,
      timetable: [
        ...draft.timetable,
        {
          id,
          title: { de: 'Neuer Act' },
          stageId: draft.stages[0]?.id ?? 'stage-unassigned',
          startsAt: startsAt.toISOString(),
          endsAt: endsAt.toISOString(),
        },
      ],
    });
  };

  const selectImportFile = async (file: File) => {
    const source = await file.text();
    setImportFilename(file.name);
    setImportSource(source);
    inspectImport(source, file.name);
  };

  const inspectImport = (source: string, filename: string) => {
    setImportSource(source);
    setImportError(undefined);
    try {
      const inspection = inspectTimetableImport(source, filename);
      const parsed = localPreview(source, inspection.format);
      if (!parsed.ok) {
        setImportInspection(inspection);
        setImportError(parsed.errors.slice(0, 4).map((error) => error.message).join(' · '));
        return;
      }
      setImportInspection({ ...inspection, rowCount: parsed.acts.length });
    } catch (error) {
      setImportInspection(undefined);
      setImportError(error instanceof Error ? error.message : 'Import konnte nicht gelesen werden.');
    }
  };

  return (
    <div className="content-grid">
      <main className="editor-column">
        <section className="panel" id="edition-metadata">
          <div className="panel-heading">
            <div>
              <span className="section-kicker">Edition</span>
              <h2>Grunddaten</h2>
            </div>
            <span className="pill">{draft.edition.type === 'winter' ? 'Winter LIR' : 'LIR'}</span>
          </div>
          <div className="form-grid">
            <label className="wide-field">
              Titel
              <input
                value={draft.edition.title.de}
                onChange={(event) =>
                  updateEdition({ title: { ...draft.edition.title, de: event.target.value } })
                }
              />
            </label>
            <label>
              Variante
              <select
                value={draft.edition.type}
                onChange={(event) => updateEdition({ type: event.target.value as 'summer' | 'winter' })}
              >
                <option value="summer">Sommer</option>
                <option value="winter">Winter</option>
              </select>
            </label>
            <label>
              Zeitzone
              <input
                value={draft.edition.timezone}
                onChange={(event) => updateEdition({ timezone: event.target.value })}
              />
            </label>
            <label>
              Beginn
              <input
                type="datetime-local"
                value={toLocalInput(draft.edition.startsAt)}
                onChange={(event) => updateEdition({ startsAt: fromLocalInput(event.target.value) })}
              />
            </label>
            <label>
              Ende
              <input
                type="datetime-local"
                value={toLocalInput(draft.edition.endsAt)}
                onChange={(event) => updateEdition({ endsAt: fromLocalInput(event.target.value) })}
              />
            </label>
          </div>
        </section>

        <section className="panel" id="stages">
          <div className="panel-heading">
            <div>
              <span className="section-kicker">Line-up</span>
              <h2>Stages</h2>
            </div>
            <button className="secondary-button" type="button" onClick={addStage}>
              Stage hinzufügen
            </button>
          </div>
          {draft.stages.length === 0 ? (
            <div className="empty-inline">Noch keine Stage. Vor dem Timetable zuerst eine Stage anlegen.</div>
          ) : (
            <div className="stage-list">
              {draft.stages.map((stage) => {
                const inUse = draft.timetable.some((act) => act.stageId === stage.id);
                return (
                  <div className="stage-row" id={`focus-${stage.id}`} key={stage.id}>
                    <input
                      className="color-field"
                      aria-label={`Farbe für ${stage.name.de}`}
                      type="color"
                      value={stage.color}
                      onChange={(event) => updateStage(stage.id, { color: event.target.value })}
                    />
                    <input
                      aria-label="Stage-Bezeichnung"
                      value={stage.name.de}
                      onChange={(event) =>
                        updateStage(stage.id, { name: { ...stage.name, de: event.target.value } })
                      }
                    />
                    <input
                      aria-label="Stage-Kurzname"
                      value={stage.shortName ?? ''}
                      placeholder="Kurzname"
                      onChange={(event) => updateStage(stage.id, { shortName: event.target.value || undefined })}
                    />
                    <code>{stage.id}</code>
                    <button
                      className="icon-danger"
                      type="button"
                      disabled={inUse}
                      title={inUse ? 'Stage wird im Timetable verwendet' : 'Stage löschen'}
                      onClick={() => removeStage(stage.id)}
                    >
                      ×
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        <section className="panel timetable-panel" id="timetable">
          <div className="panel-heading">
            <div>
              <span className="section-kicker">Programm</span>
              <h2>Timetable</h2>
            </div>
            <div className="heading-actions">
              <button className="secondary-button" type="button" onClick={() => setImportOpen(true)}>
                CSV / JSON importieren
              </button>
              <button className="primary-button" type="button" onClick={addAct}>
                Act hinzufügen
              </button>
            </div>
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Act</th>
                  <th>Stage</th>
                  <th>Start</th>
                  <th>Ende</th>
                  <th>Tags</th>
                  <th aria-label="Aktionen" />
                </tr>
              </thead>
              <tbody>
                {sortedActs.map((act) => (
                  <tr id={`focus-${act.id}`} key={act.id}>
                    <td>
                      <input
                        aria-label="Act-Name"
                        value={act.title.de}
                        onChange={(event) =>
                          updateAct(act.id, { title: { ...act.title, de: event.target.value } })
                        }
                      />
                      <code>{act.id}</code>
                    </td>
                    <td>
                      <select
                        aria-label="Stage"
                        value={act.stageId}
                        onChange={(event) => updateAct(act.id, { stageId: event.target.value })}
                      >
                        {!draft.stages.some((stage) => stage.id === act.stageId) && (
                          <option value={act.stageId}>Nicht zugeordnet</option>
                        )}
                        {draft.stages.map((stage) => (
                          <option key={stage.id} value={stage.id}>
                            {stage.name.de}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <input
                        aria-label="Startzeit"
                        type="datetime-local"
                        value={toLocalInput(act.startsAt)}
                        onChange={(event) =>
                          updateAct(act.id, { startsAt: fromLocalInput(event.target.value) })
                        }
                      />
                    </td>
                    <td>
                      <input
                        aria-label="Endzeit"
                        type="datetime-local"
                        value={toLocalInput(act.endsAt)}
                        onChange={(event) =>
                          updateAct(act.id, { endsAt: fromLocalInput(event.target.value) })
                        }
                      />
                    </td>
                    <td>
                      <input
                        aria-label="Tags"
                        value={act.tags?.join(', ') ?? ''}
                        placeholder="ambient, live"
                        onChange={(event) =>
                          updateAct(act.id, {
                            tags: event.target.value
                              .split(',')
                              .map((tag) => tag.trim())
                              .filter(Boolean),
                          })
                        }
                      />
                    </td>
                    <td>
                      <button
                        className="icon-danger"
                        type="button"
                        aria-label={`${act.title.de} löschen`}
                        onClick={() =>
                          onChange({
                            ...draft,
                            timetable: draft.timetable.filter((candidate) => candidate.id !== act.id),
                          })
                        }
                      >
                        ×
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </main>

      <aside className="review-column">
        <section className="panel sticky-panel">
          <div className="panel-heading compact">
            <div>
              <span className="section-kicker">Qualitätscheck</span>
              <h2>Veröffentlichen</h2>
            </div>
          </div>
          <div className="review-actions">
            <button className="secondary-button" type="button" onClick={() => setPreviewOpen(true)}>
              Vorschau
            </button>
            <button className="secondary-button" type="button" disabled={validating} onClick={onValidate}>
              {validating ? 'Prüft …' : 'Validieren'}
            </button>
            <button
              className="publish-button"
              type="button"
              disabled={publishing || validation?.valid !== true}
              onClick={onPublish}
            >
              {publishing ? 'Wird veröffentlicht …' : 'Release veröffentlichen'}
            </button>
          </div>
          {validation === undefined ? (
            <p className="muted">Vor dem Release muss der Entwurf geprüft werden.</p>
          ) : validation.issues.length === 0 ? (
            <div className="success-box">Bereit zur Veröffentlichung.</div>
          ) : (
            <>
              <div className="validation-summary">
                <span className="error-count">{issueCounts.errors} Fehler</span>
                <span className="warning-count">{issueCounts.warnings} Hinweise</span>
              </div>
              <ol className="issue-list">
                {validation.issues.map((issue, index) => (
                  <li key={`${issue.code}-${index}`}>
                    <button type="button" onClick={() => onFocusIssue(issue)}>
                      <span className={`severity-dot ${issue.severity}`} />
                      <span>
                        <strong>{issue.message}</strong>
                        <small>{issue.path.join(' → ')}</small>
                      </span>
                    </button>
                  </li>
                ))}
              </ol>
            </>
          )}
          <hr />
          <h3>Revisionen</h3>
          <div className="revision-list">
            {revisions.length === 0 && <p className="muted">Noch kein Release.</p>}
            {revisions.map((revision) => (
              <div className="revision-row" key={revision.releaseId}>
                <div>
                  <strong>v{revision.version}</strong>
                  {revision.active && <span className="tiny-pill">Aktiv</span>}
                  <small>{new Date(revision.publishedAt).toLocaleString('de-DE')}</small>
                </div>
                <button
                  className="text-button"
                  type="button"
                  disabled={revision.active}
                  onClick={() => onRestore(revision.releaseId)}
                >
                  Als Entwurf
                </button>
              </div>
            ))}
          </div>
        </section>
      </aside>

      {importOpen && (
        <div className="modal-backdrop" role="presentation" onMouseDown={() => setImportOpen(false)}>
          <section
            className="modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="import-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="modal-heading">
              <div>
                <span className="section-kicker">Timetable</span>
                <h2 id="import-title">CSV oder JSON importieren</h2>
              </div>
              <button className="modal-close" type="button" onClick={() => setImportOpen(false)}>
                ×
              </button>
            </div>
            <input
              ref={fileRef}
              className="visually-hidden"
              type="file"
              accept=".csv,.json,text/csv,application/json"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file !== undefined) void selectImportFile(file);
              }}
            />
            <button className="upload-drop full" type="button" onClick={() => fileRef.current?.click()}>
              Datei auswählen
              <small>CSV-Vertrag oder JSON mit einem `timetable`-Array</small>
            </button>
            <label>
              Oder Inhalt einfügen
              <textarea
                className="import-source"
                rows={10}
                value={importSource}
                onChange={(event) => inspectImport(event.target.value, importFilename)}
                placeholder="id,titleDe,stageId,startsAt,endsAt"
              />
            </label>
            {importError !== undefined && <div className="error-box">{importError}</div>}
            {importInspection !== undefined && importError === undefined && (
              <div className="import-summary">
                <strong>{importInspection.rowCount} Acts erkannt</strong>
                <span>{importInspection.format.toUpperCase()}</span>
                <small>{importInspection.columns.join(' · ')}</small>
              </div>
            )}
            <div className="modal-actions">
              <button className="secondary-button" type="button" onClick={() => setImportOpen(false)}>
                Abbrechen
              </button>
              <button
                className="primary-button"
                type="button"
                disabled={importInspection === undefined || importError !== undefined || importing}
                onClick={() => {
                  if (importInspection === undefined) return;
                  void onImport(importSource, importInspection.format)
                    .then(() => setImportOpen(false))
                    .catch((error: unknown) =>
                      setImportError(error instanceof Error ? error.message : 'Import fehlgeschlagen.'),
                    );
                }}
              >
                {importing ? 'Importiert …' : 'Entwurf ersetzen'}
              </button>
            </div>
          </section>
        </div>
      )}

      {previewOpen && (
        <div className="modal-backdrop preview-backdrop" role="presentation" onMouseDown={() => setPreviewOpen(false)}>
          <section
            className="preview-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Release-Vorschau"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <button className="modal-close floating-close" type="button" onClick={() => setPreviewOpen(false)}>
              ×
            </button>
            <div className="phone-preview">
              <div className="preview-hero">
                <span>INOFFIZIELLER LIR COMPANION</span>
                <h2>{draft.edition.title.de}</h2>
                <p>
                  {new Date(draft.edition.startsAt).toLocaleDateString('de-DE')} –{' '}
                  {new Date(draft.edition.endsAt).toLocaleDateString('de-DE')}
                </p>
              </div>
              <div className="preview-stats">
                <span>{draft.timetable.length} Acts</span>
                <span>{draft.stages.length} Stages</span>
                <span>{draft.map.features.features.length} Orte</span>
              </div>
              <div className="preview-list">
                <h3>Nächste Acts</h3>
                {sortedActs.slice(0, 8).map((act) => (
                  <article key={act.id}>
                    <time>{new Date(act.startsAt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}</time>
                    <div>
                      <strong>{act.title.de}</strong>
                      <span>{draft.stages.find((stage) => stage.id === act.stageId)?.name.de ?? act.stageId}</span>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
